import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEsMovil } from '../../../hooks/useEsMovil';
import { CalendarDays, CalendarRange, List, LayoutGrid, CalendarPlus, Users, Sun, Clock, AlertTriangle, TrendingDown, CalendarX2, Plus, ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import { getCalendarFresh, getCalendarTodas, readCalendarCache, CalBlock, DAYS_SHORT, hhmm, isActiveOn, ymd } from '../api';
import TimeGrid, { TimeGridBlock, TimeGridColumn } from '../../../components/TimeGrid/TimeGrid';
import { isRentaOEnsayo } from '../../../utils/classKind';
import { useAuth } from '../../../context/AuthContext';
import ClassForm, { ClassFormInitial } from '../components/ClassForm';
import ClassCover from '../components/ClassCover';
import { colorClase } from '../../../utils/classColor';

// Orden Lun..Dom (1,2,3,4,5,6,0)
const ORDER = [1, 2, 3, 4, 5, 6, 0];
import { nivelOcupacion } from '../../../utils/ocupacion';

const selCls = 'rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';

function cupoText(enrolled: number, cap: number | null) {
  if (!cap) return `${enrolled}`;
  return `${enrolled}/${cap}`;
}
function cupoTone(enrolled: number, cap: number | null) {
  if (!cap) return 'text-content-3';
  // Escala única con el reporte de ocupación (utils/ocupacion.ts): llena = verde,
  // sobrecupo = ámbar (lo único que hay que vigilar), poco llena = rojo.
  switch (nivelOcupacion((enrolled / cap) * 100)) {
    case 'sobrecupo': return 'text-amber-600 dark:text-amber-400 font-semibold';
    case 'alta': return 'text-emerald-600 dark:text-emerald-400 font-semibold';
    case 'baja': return 'text-red-600 dark:text-red-400';
    default: return 'text-content-2';
  }
}
function toMin(t: string) { const [h, m] = t.split(':'); return (+h) * 60 + (+m); }

// Chip "Terminó 17 jul" para las clases cuyo ciclo ya venció (end_date pasada).
// No las escondemos del listado: desde ahí se clonan al ciclo nuevo.
function EndedChip({ end }: { end?: string | null }) {
  if (!end || end >= ymd(new Date())) return null;
  const d = new Date(`${end}T00:00:00`);
  return (
    <span className="shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-medium text-content-3"
      title={`El ciclo de esta clase terminó el ${end}`}>
      Terminó {d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
    </span>
  );
}
/** Ventana (min) antes del inicio para considerar una clase "próxima a comenzar". */
const SOON_WINDOW = 30;
const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function ClassesCalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Pinta al instante lo último conocido (caché local) y refresca por detrás.
  const [blocks, setBlocks] = useState<CalBlock[]>(() => readCalendarCache() || []);
  const [loading, setLoading] = useState(() => !readCalendarCache());
  const [searchParams] = useSearchParams();
  // La semana son 7 columnas: en un teléfono NO cabe y se arrastra (el TimeGrid ya tiene su
  // `overflow-x-auto`). Con 128px por día se veían DOS días enteros y el corte caía tan limpio
  // que parecía el final de la semana; con 104 asoma el tercero a medias, que es lo que dice
  // «esto sigue». El escritorio se queda igual.
  const esMovil = useEsMovil();
  // Default: SEMANA en escritorio (como Kydemy: al entrar a Clases se ve la semana completa) y
  // HOY en teléfono (Carlos, 19-sep-2026). En 390px la semana enseña 2 días de 7 y hay que
  // arrastrar para ver el resto: abrir en el día es lo útil desde el móvil, y «Semana» sigue a
  // un toque. Solo decide con qué vista ABRE; en cuanto se toca otra, manda el cajero.
  // ?vista=hoy|semana|mes|tarjetas|lista siguen forzando la suya (enlaces del Home, etc.).
  // Vistas (Karla 29-jul, video 16:56: «me saldrían aquí semana, mes, tarjeta, tabla… pásate a la
  // semana siguiente o en su defecto al siguiente mes para poder visualizar las clases»).
  const [view, setView] = useState<'hoy' | 'semana' | 'mes' | 'cards' | 'list'>(() => {
    const v = searchParams.get('vista');
    return v === 'hoy' ? 'hoy' : v === 'mes' ? 'mes' : v === 'tarjetas' || v === 'cards' ? 'cards'
      : v === 'lista' || v === 'list' || v === 'tabla' ? 'list'
      : v === 'semana' ? 'semana' : esMovil ? 'hoy' : 'semana';
  });
  // Mes navegado en la vista Mes (día 1 del mes que se está viendo).
  const [mesVisto, setMesVisto] = useState<Date>(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  // Estado de la clase: Activa (vigente hoy) · Planificada (su ciclo aún no arranca) · Archivada.
  // Nuestra BD no tiene 'planned': se deduce de la vigencia, que es lo que significa en Kydemy.
  const [fEstado, setFEstado] = useState<'todas' | 'activas' | 'planificadas' | 'archivadas'>('todas');
  const [conArchivadas, setConArchivadas] = useState(false);
  const [selDate, setSelDate] = useState<Date>(() => new Date()); // día navegado en la vista "Hoy"
  const dateRef = useRef<HTMLInputElement>(null);
  const [fTeacher, setFTeacher] = useState('');
  const [fStyle, setFStyle] = useState('');
  const [fRoom, setFRoom] = useState('');
  // Ocultar rentas/ensayos/privadas del grid semanal. Default VISIBLE (atenuadas):
  // una renta = salón ocupado; ocultarlas de fábrica invita a agendar encima.
  // La preferencia se recuerda por dispositivo (como el tema).
  const [hideRentals, setHideRentals] = useState(() => localStorage.getItem('lm-hide-rentas') === '1');
  const toggleRentals = (v: boolean) => { setHideRentals(v); localStorage.setItem('lm-hide-rentas', v ? '1' : '0'); };
  const [showForm, setShowForm] = useState(false);
  // Valores con los que abrir el alta (clic en un hueco del calendario). null = alta en blanco.
  const [formInitial, setFormInitial] = useState<ClassFormInitial | null>(null);
  const esAdmin = user?.role === 'admin';
  const hhmmDeMin = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  /** Clic en un hueco del grid (Karla 30-jul, como Kydemy): abre el alta con día y hora puestos. */
  function abrirAltaEn(ini: ClassFormInitial) { setFormInitial(ini); setShowForm(true); }
  function abrirAltaVacia() { setFormInitial(null); setShowForm(true); }

  function load() { getCalendarFresh().then(setBlocks).finally(() => setLoading(false)); }
  useEffect(() => { load(); }, []);

  // Las archivadas no vienen por defecto (el calendario y el screensaver no deben pintarlas):
  // se piden solo cuando el filtro las necesita, y ya no se vuelven a soltar.
  useEffect(() => {
    if (fEstado !== 'archivadas' && fEstado !== 'todas') return;
    if (fEstado === 'todas' && !conArchivadas) return;
    if (conArchivadas) return;
    if (fEstado === 'archivadas') {
      getCalendarTodas().then((r) => { setBlocks(r.data); setConArchivadas(true); }).catch(() => {});
    }
  }, [fEstado, conArchivadas]);

  // inscritos por clase (del calendario: cuenta real de class_students de TODAS las clases)
  const enrolledOf = useMemo(() => {
    const m: Record<number, number> = {};
    blocks.forEach((b) => (m[b.class_id] = b.enrolled));
    return m;
  }, [blocks]);

  const teachers = useMemo(() => [...new Set(blocks.map((b) => b.teacher).filter(Boolean))].sort() as string[], [blocks]);
  const styles = useMemo(() => [...new Set(blocks.map((b) => b.style).filter(Boolean))].sort() as string[], [blocks]);
  const rooms = useMemo(() => [...new Set(blocks.map((b) => b.classroom).filter(Boolean))].sort() as string[], [blocks]);

  /** Estado de una clase, con la semántica de Kydemy: archivada · planificada (su ciclo aún no
   *  arranca) · activa (vigente hoy). Una clase cuyo ciclo YA terminó también cuenta como archivada
   *  a efectos del filtro, aunque siga con status 'active': para ella es historia. */
  const estadoDe = (b: CalBlock): 'activas' | 'planificadas' | 'archivadas' => {
    const hoy = ymd(new Date());
    if (b.status === 'archived') return 'archivadas';
    if (b.start_date && b.start_date > hoy) return 'planificadas';
    if (b.end_date && b.end_date < hoy) return 'archivadas';
    return 'activas';
  };

  // Tarjetas y tabla son CATÁLOGO (ahí sí manda el filtro de estado). Hoy/Semana/Mes son
  // CALENDARIO: una clase archivada no ocupa un horario, así que nunca se pinta ahí —
  // ni cuando el filtro quedó en «archivadas» ni cuando ya se cargaron para el catálogo.
  const esCatalogo = view === 'cards' || view === 'list';
  const filtered = useMemo(() => blocks.filter((b) => {
    if (fTeacher && b.teacher !== fTeacher) return false;
    if (fStyle && b.style !== fStyle) return false;
    if (fRoom && b.classroom !== fRoom) return false;
    if (esCatalogo) return fEstado === 'todas' || estadoDe(b) === fEstado;
    return b.status !== 'archived';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [blocks, fTeacher, fStyle, fRoom, fEstado, esCatalogo]);

  const byDay = (wd: number) => filtered.filter((b) => b.weekday === wd).sort((a, b) => a.start_time.localeCompare(b.start_time));

  const classRows = useMemo(() => {
    const m = new Map<number, { b: CalBlock; days: Set<number> }>();
    for (const b of filtered) {
      const e = m.get(b.class_id) || { b, days: new Set<number>() };
      e.days.add(b.weekday);
      m.set(b.class_id, e);
    }
    const hoy = ymd(new Date());
    return [...m.values()]
      .map(({ b, days }) => ({ ...b, days: ORDER.filter((d) => days.has(d)) }))
      // Vigentes primero: el listado sigue mostrando los ciclos terminados (son historia
      // y desde ahí se clonan), pero abajo y marcados.
      .sort((a, b) => {
        const av = isActiveOn(a, hoy) ? 0 : 1, bv = isActiveOn(b, hoy) ? 0 : 1;
        return av !== bv ? av - bv : a.name.localeCompare(b.name);
      });
  }, [filtered]);

  // ===== Día seleccionado (navegable: ayer/hoy/mañana o calendario) =====
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const isToday = selDate.toDateString() === now.toDateString();
  const selWd = selDate.getDay();
  // ── Ciclo terminado: si TODAS las clases vencieron, el calendario vacío no es un error
  // (es lo que muestra Kydemy). Explicarlo y decir qué falta: clonar al ciclo nuevo.
  // Dos casos distintos de "hoy no hay nada": el ciclo YA TERMINÓ (falta clonar) o el ciclo
  // nuevo AÚN NO ARRANCA (ya se clonó, empieza el lunes). Decir el equivocado confunde.
  const cicloTerminado = useMemo(() => {
    if (!blocks.length) return null;
    const hoy = ymd(new Date());
    if (blocks.some((b) => isActiveOn(b, hoy))) return null;
    const clases = new Set(blocks.map((b) => b.class_id)).size;
    const starts = (blocks.map((b) => b.start_date).filter(Boolean) as string[]).sort();
    if (starts.length && starts[0] > hoy) return { proximo: starts[0], clases };
    const ends = (blocks.map((b) => b.end_date).filter(Boolean) as string[]).sort();
    if (!ends.length) return null;
    return { last: ends[ends.length - 1], clases };
  }, [blocks]);

  const selYmd = ymd(selDate);
  // Solo las clases VIGENTES en la fecha navegada (start_date/end_date, como Kydemy).
  const dayClasses = useMemo(
    () => byDay(selWd).filter((b) => isActiveOn(b, selYmd))
      .map((b) => ({ ...b, enrolled: enrolledOf[b.class_id] ?? 0 })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, enrolledOf, selWd, selYmd]
  );
  // El "siguiente" y los estados de tiempo real solo aplican a HOY.
  const nextIdx = isToday ? dayClasses.findIndex((c) => nowMin < toMin(c.start_time)) : -1;
  const summary = useMemo(() => {
    const withCap = dayClasses.filter((c) => c.capacity && c.enrolled > 0);
    const cap = withCap.reduce((s, c) => s + (c.capacity || 0), 0);
    const enrCap = withCap.reduce((s, c) => s + c.enrolled, 0);
    return {
      count: dayClasses.length,
      expected: dayClasses.reduce((s, c) => s + c.enrolled, 0),
      occ: cap ? Math.round((enrCap / cap) * 100) : 0,
    };
  }, [dayClasses]);
  const fullDay = dayClasses.filter((c) => c.capacity && c.enrolled >= c.capacity);
  const lowDay = dayClasses.filter((c) => c.capacity && c.enrolled > 0 && c.enrolled / c.capacity < 0.5);

  function shiftDay(delta: number) {
    setSelDate((d) => { const n = new Date(d); n.setHours(0, 0, 0, 0); n.setDate(n.getDate() + delta); return n; });
  }
  const dayLabel = capitalize(selDate.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' }));
  const pad = (n: number) => String(n).padStart(2, '0');
  const dateInputValue = `${selDate.getFullYear()}-${pad(selDate.getMonth() + 1)}-${pad(selDate.getDate())}`;

  function statusBadge(c: { start_time: string; end_time: string }, i: number) {
    if (!isToday) return null; // sin estados de tiempo real en días que no son hoy
    const s = toMin(c.start_time), e = toMin(c.end_time);
    if (nowMin >= s && nowMin < e) return <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">● En curso</span>;
    const mins = s - nowMin;
    if (nowMin < s && mins <= SOON_WINDOW) return <span className="rounded-full bg-brand-blue/15 px-2 py-0.5 text-[11px] font-semibold text-brand-blue">Pronto · en {mins} min</span>;
    if (i === nextIdx) return <span className="rounded-full bg-accent/20 px-2 py-0.5 text-[11px] font-semibold text-accent-ink">Siguiente</span>;
    if (nowMin >= e) return <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-medium text-content-3">Terminó</span>;
    return null;
  }

  const toggle = (v: typeof view, Icon: typeof Sun, label: string) => (
    <button onClick={() => setView(v)} title={label}
      className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-sm font-medium transition ${view === v ? 'bg-accent text-on-accent' : 'text-content-2 hover:bg-surface-2'}`}>
      <Icon size={15} /> {label}
    </button>
  );

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-content">
          <CalendarDays className="text-brand-blue" size={24} /> Clases <span className="font-normal text-content-3">— {
            view === 'hoy' ? (isToday ? 'hoy' : 'por día')
            : view === 'semana' ? 'semana'
            : view === 'mes' ? 'mes'
            : view === 'cards' ? 'tarjetas' : 'tabla'}</span>
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {view !== 'hoy' && (
            <>
              <select value={fTeacher} onChange={(e) => setFTeacher(e.target.value)} className={selCls}>
                <option value="">Todos los profes</option>
                {teachers.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={fStyle} onChange={(e) => setFStyle(e.target.value)} className={selCls}>
                <option value="">Todos los estilos</option>
                {styles.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={fRoom} onChange={(e) => setFRoom(e.target.value)} className={selCls}>
                <option value="">Todos los salones</option>
                {rooms.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </>
          )}
          {/* Estado de la clase — solo en tabla/tarjetas: filtrar el calendario por «archivadas»
              no querría decir nada (una clase archivada no ocupa un horario). */}
          {(view === 'list' || view === 'cards') && (
            <select value={fEstado} onChange={(e) => setFEstado(e.target.value as typeof fEstado)} className={selCls}
              title="Activa = vigente hoy · Planificada = su ciclo aún no arranca · Archivada = fuera de servicio o ciclo terminado">
              <option value="todas">Todos los estados</option>
              <option value="activas">Activas</option>
              <option value="planificadas">Planificadas</option>
              <option value="archivadas">Archivadas</option>
            </select>
          )}
          {view === 'semana' && (
            <label className="flex cursor-pointer select-none items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content-2">
              <input type="checkbox" checked={hideRentals} onChange={(e) => toggleRentals(e.target.checked)}
                className="h-3.5 w-3.5 accent-[rgb(var(--c-accent-solid))]" />
              Ocultar rentas y ensayos
            </label>
          )}
          {/* 🚨 19-sep-2026: los cinco modos medían más que un teléfono de 390px y «Tabla»
              se salía de la pantalla SIN scroll que la alcanzara — desde el móvil esa vista no
              existía. `flex-wrap` la baja al segundo renglón; en escritorio no cambia nada. */}
          <div className="flex max-w-full flex-wrap rounded-lg border border-line bg-surface p-0.5">
            {toggle('hoy', Sun, 'Hoy')}
            {toggle('semana', CalendarRange, 'Semana')}
            {toggle('mes', CalendarPlus, 'Mes')}
            {toggle('cards', LayoutGrid, 'Tarjetas')}
            {toggle('list', List, 'Tabla')}
          </div>
          {user?.role === 'admin' && (
            <button onClick={() => navigate('/clases/clonar')} title="Clonar las clases al ciclo nuevo"
              className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2">
              <Copy size={15} /> Clonar ciclo
            </button>
          )}
          {user?.role === 'admin' && (
            <button onClick={abrirAltaVacia}
              className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
              <Plus size={16} /> Nueva clase
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <ClassForm
          initial={formInitial || undefined}
          onClose={() => { setShowForm(false); setFormInitial(null); }}
          onSaved={(id) => { setShowForm(false); setFormInitial(null); navigate(`/clases/${id}`); }}
        />
      )}

      {/* Calendario vacío porque el ciclo terminó: decirlo con claridad (nada se perdió) */}
      {/* Habla de HOY: si navegaste a un día que sí tiene clases, el aviso estorba */}
      {!loading && cicloTerminado && dayClasses.length === 0 && (
        <div className="mb-4 flex flex-wrap items-start gap-3 rounded-xl border border-line bg-surface-2/60 px-4 py-3">
          <CalendarX2 size={18} className="mt-0.5 shrink-0 text-content-3" />
          <div className="min-w-0 flex-1 text-sm">
            {cicloTerminado.proximo ? (
              <>
                <p className="font-semibold text-content">
                  El ciclo nuevo arranca el {new Date(`${cicloTerminado.proximo}T00:00:00`)
                    .toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
                <p className="mt-0.5 text-content-2">
                  Por eso el calendario de hoy sale vacío. Tus {cicloTerminado.clases} clases ya están
                  listas con sus alumnos y horarios: puedes inscribir gente desde ahora y aparecerán
                  en el calendario ese día.
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold text-content">
                  El ciclo anterior terminó el {new Date(`${cicloTerminado.last}T00:00:00`)
                    .toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}
                </p>
                <p className="mt-0.5 text-content-2">
                  Por eso el calendario sale vacío. Tus {cicloTerminado.clases} clases siguen aquí con sus
                  alumnos, horarios e historial; volverán al calendario cuando se clonen al ciclo nuevo con
                  sus fechas de inicio y fin.
                </p>
              </>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <button onClick={() => setView('list')}
              className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-content-2 transition hover:bg-surface-2">
              Ver las clases
            </button>
            {/* Si el ciclo nuevo ya está creado y solo falta que arranque, clonar no es el paso siguiente */}
            {user?.role === 'admin' && !cicloTerminado.proximo && (
              <button onClick={() => navigate('/clases/clonar')}
                className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-xs font-semibold text-on-accent transition hover:bg-accent-solid/90">
                <Copy size={13} /> Clonar al ciclo nuevo
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <div className="space-y-2 lg:col-span-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 w-full animate-pulse rounded-xl bg-skeleton" />)}</div>
          <div className="h-48 w-full animate-pulse rounded-xl bg-skeleton" />
        </div>
      ) : view === 'hoy' ? (
        /* ===== HOY ===== */
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Clases del día (navegable) */}
          <div className="min-w-0 lg:col-span-2">
            {/* Barra de navegación: ‹ fecha (clic = calendario) › + Hoy */}
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1">
                <button onClick={() => shiftDay(-1)} aria-label="Día anterior"
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-content-2 ring-1 ring-line transition hover:bg-surface-2 hover:text-content">
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => { const el = dateRef.current; if (!el) return; try { (el as unknown as { showPicker: () => void }).showPicker(); } catch { el.focus(); } }}
                  className="relative flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm font-semibold text-content ring-1 ring-line transition hover:bg-surface-2">
                  <CalendarDays size={15} className="text-accent-ink" />
                  <span>{dayLabel}</span>
                  <input ref={dateRef} type="date" value={dateInputValue}
                    onChange={(e) => { const v = e.target.value; if (!v) return; const [y, m, d] = v.split('-').map(Number); setSelDate(new Date(y, m - 1, d)); }}
                    className="pointer-events-none absolute inset-0 h-0 w-0 opacity-0" tabIndex={-1} />
                </button>
                <button onClick={() => shiftDay(1)} aria-label="Día siguiente"
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-content-2 ring-1 ring-line transition hover:bg-surface-2 hover:text-content">
                  <ChevronRight size={18} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                {!isToday && (
                  <button onClick={() => setSelDate(new Date())}
                    className="rounded-md bg-accent/15 px-2.5 py-1 text-xs font-semibold text-accent-ink ring-1 ring-accent/25 transition hover:bg-accent/25">Hoy</button>
                )}
                <span className="text-xs text-content-3">{dayClasses.length} clase{dayClasses.length === 1 ? '' : 's'}</span>
                {/* Esta vista es una LISTA (no el grid de horas): el equivalente al «+» del
                    calendario es dar de alta directo en el día que estás viendo. */}
                {esAdmin && (
                  <button onClick={() => abrirAltaEn({ weekday: selWd })} title="Agregar una clase en este día"
                    className="flex items-center gap-1 rounded-md bg-accent/15 px-2 py-1 text-xs font-semibold text-accent-ink ring-1 ring-accent/25 transition hover:bg-accent/25">
                    <Plus size={13} /> Agregar
                  </button>
                )}
              </div>
            </div>
            {dayClasses.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-2xl border border-line bg-surface p-10 text-center shadow-sm">
                <CalendarX2 size={28} className="text-content-3" />
                <div className="font-medium text-content">Día libre</div>
                <div className="text-sm text-content-3">No hay clases programadas para este día.</div>
                {esAdmin && (
                  <button onClick={() => abrirAltaEn({ weekday: selWd })}
                    className="mt-1 flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
                    <Plus size={15} /> Agregar una clase
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {dayClasses.map((c, i) => {
                  const full = c.capacity && c.enrolled >= c.capacity;
                  const pct = c.capacity ? Math.min(100, Math.round((c.enrolled / c.capacity) * 100)) : 0;
                  const done = isToday && nowMin >= toMin(c.end_time);
                  return (
                    <button key={c.schedule_id} onClick={() => navigate(`/clases/${c.class_id}`)}
                      className={`flex w-full items-center gap-3 rounded-xl border bg-surface p-3 text-left shadow-sm transition hover:border-accent/40 hover:bg-surface-2/40 ${i === nextIdx ? 'border-accent/50' : 'border-line'} ${done ? 'opacity-70' : ''}`}>
                      <span className="h-12 w-1.5 shrink-0 rounded-full" style={{ background: colorClase(c.color) }} />
                      {/* 🚨 19-sep-2026: en un teléfono, entre la hora (80px), el cupo (96) y la
                          insignia de estado, al NOMBRE le quedaban ~50px y salía «K-P…», «Hip…»:
                          la lista del día se leía como cuatro clases sin nombre. En móvil se
                          estrechan las dos columnas fijas, la insignia baja de renglón si estorba
                          y el nombre puede usar DOS líneas. El escritorio queda igual. */}
                      <div className="w-14 shrink-0 sm:w-20">
                        <div className="font-display text-sm font-bold text-content">{hhmm(c.start_time)}</div>
                        <div className="text-[11px] text-content-3">{hhmm(c.end_time)}</div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="line-clamp-2 font-medium text-content sm:truncate">{c.name}</span>
                          {statusBadge(c, i)}
                        </div>
                        <div className="truncate text-xs text-content-3">{c.teacher || 'Sin profe'}{c.classroom ? ` · ${c.classroom}` : ''}</div>
                      </div>
                      <div className="w-16 shrink-0 text-right sm:w-24">
                        <div className={`text-sm tabular-nums ${cupoTone(c.enrolled, c.capacity)}`}>
                          <Users size={11} className="mr-0.5 inline" />{cupoText(c.enrolled, c.capacity)}{full ? ' · llena' : ''}
                        </div>
                        {c.capacity ? (
                          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
                            <div className="h-full rounded-full bg-content" style={{ width: `${pct}%` }} />
                          </div>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Panel lateral: resumen + atención */}
          <div className="min-w-0 space-y-4">
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-content-2"><Clock size={16} className="text-brand-blue" /> Resumen del día</h3>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><div className="font-display text-2xl font-bold text-content">{summary.count}</div><div className="text-[11px] text-content-3">clases</div></div>
                <div><div className="font-display text-2xl font-bold text-content">{summary.expected}</div><div className="text-[11px] text-content-3">alumnos esperados</div></div>
                <div><div className="font-display text-2xl font-bold text-content">{summary.occ}%</div><div className="text-[11px] text-content-3">ocupación</div></div>
              </div>
            </div>

            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-content-2"><AlertTriangle size={16} className="text-amber-500" /> Requiere atención</h3>
              {fullDay.length === 0 && lowDay.length === 0 ? (
                <p className="text-sm text-content-3">Todo en orden por hoy. 👌</p>
              ) : (
                <div className="space-y-3">
                  {fullDay.length > 0 && (
                    <div>
                      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-accent-ink"><Users size={13} /> Llenas / sobrecupo ({fullDay.length})</div>
                      <ul className="space-y-1">
                        {fullDay.map((c) => (
                          <li key={c.schedule_id}><button onClick={() => navigate(`/clases/${c.class_id}`)} className="flex w-full items-center justify-between gap-2 text-sm hover:underline">
                            <span className="truncate text-content">{c.name}</span>
                            <span className="shrink-0 font-semibold text-accent-ink">{cupoText(c.enrolled, c.capacity)}</span>
                          </button></li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {lowDay.length > 0 && (
                    <div>
                      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-content-2"><TrendingDown size={13} /> Con cupo por llenar ({lowDay.length})</div>
                      <ul className="space-y-1">
                        {lowDay.map((c) => (
                          <li key={c.schedule_id}><button onClick={() => navigate(`/clases/${c.class_id}`)} className="flex w-full items-center justify-between gap-2 text-sm hover:underline">
                            <span className="truncate text-content">{c.name}</span>
                            <span className="shrink-0 text-content-2">{cupoText(c.enrolled, c.capacity)}</span>
                          </button></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : view === 'semana' ? (
        /* ===== SEMANA (grid estilo Kydemy: columnas Lun→Dom × eje de horas) ===== */
        (() => {
          // Semana NAVEGABLE: se ancla en `selDate` (la misma que usa la vista Hoy), así que
          // moverse a otra semana o cambiar de vista conserva el contexto. Pedido de Karla
          // 29-jul: necesitaba ver la semana del 3-ago para inscribir antes de que arranque.
          const monday = new Date(selDate);
          monday.setHours(0, 0, 0, 0);
          monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
          const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
          const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
          const weekHasToday = hoy >= monday && hoy <= sunday;
          const todayWd = weekHasToday ? hoy.getDay() : -1;
          const cols: TimeGridColumn[] = ORDER.map((wd, i) => {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            return {
              id: String(wd),
              highlight: wd === todayWd,
              label: (
                <div>
                  <div className="uppercase tracking-wide">{DAYS_SHORT[wd]}</div>
                  <div className="font-display text-base font-bold">{d.getDate()}</div>
                </div>
              ),
            };
          });
          // Ensayos/rentas vienen del ETL como CLASES distintas con el mismo nombre y horarios
          // traslapados el mismo día (ENSAYO BALLET ×5: 13:00-16:00/-17:00/-17:30/13:15-14:15…),
          // lo que produce astillas ilegibles. Fusionar por nombre+día los spans que se traslapan,
          // como los pinta Kydemy. Spans disjuntos (renta 13:30 y 16:30) sí quedan separados.
          // Vigencia por COLUMNA: cada día de la semana es una fecha concreta, así que un
          // ciclo que terminó el 17-jul no pinta nada en la semana del 27-jul.
          const dateOfWd: Record<number, string> = {};
          ORDER.forEach((wd, i) => {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            dateOfWd[wd] = ymd(d);
          });
          const vigentes = filtered.filter((b) => isActiveOn(b, dateOfWd[b.weekday]));
          const merged: CalBlock[] = [];
          const byDayClass = new Map<string, CalBlock[]>();
          for (const b of vigentes) {
            const k = `${b.weekday}-${(b.name || '').trim().toLowerCase()}`;
            if (!byDayClass.has(k)) byDayClass.set(k, []);
            byDayClass.get(k)!.push(b);
          }
          for (const group of byDayClass.values()) {
            group.sort((a, b2) => toMin(a.start_time) - toMin(b2.start_time));
            let cur = { ...group[0] };
            for (let i = 1; i < group.length; i++) {
              const nxt = group[i];
              if (toMin(nxt.start_time) <= toMin(cur.end_time)) {
                if (toMin(nxt.end_time) > toMin(cur.end_time)) cur.end_time = nxt.end_time;
              } else {
                merged.push(cur);
                cur = { ...nxt };
              }
            }
            merged.push(cur);
          }
          const visible = hideRentals ? merged.filter((b) => !isRentaOEnsayo(b)) : merged;
          // Una franja fija por SALÓN en cada día (Karla 29-sep: «que siempre se respete la cajita
          // por salón» para ver los huecos libres). Salen de los salones que usa la semana visible;
          // con el filtro de salón activo queda uno solo y el grid vuelve a su ancho completo.
          const salones = [...new Set(visible.map((b) => b.classroom).filter(Boolean))].sort() as string[];
          const subCols = salones.map((r) => ({ id: r, label: r.replace(/^sal[oó]n\s*/i, 'S') }));
          const gridBlocks: TimeGridBlock[] = visible.map((b) => {
            const en = enrolledOf[b.class_id] ?? 0;
            const s = toMin(b.start_time), e = toMin(b.end_time);
            const isToday2 = b.weekday === todayWd;
            const muted = isRentaOEnsayo(b);
            return {
              key: String(b.schedule_id),
              colId: String(b.weekday),
              subCol: b.classroom || undefined,
              startMin: s,
              endMin: e,
              color: b.color,
              muted,
              dimmed: isToday2 && nowMin >= e,
              ring: isToday2 && nowMin >= s && nowMin < e,
              onClick: () => navigate(`/clases/${b.class_id}`),
              // Cajita (Karla 29-sep-2026, 2º video): SIN la hora —ya la da el eje—, el nombre
              // COMPLETO (se parte en renglones en vez de cortarse con «…») y todo en MAYÚSCULAS
              // para un mismo formato; el «(Grupo A)» baja a su propio renglón: «8:30 F+60 / GRUPO A».
              content: (() => {
                const [, base, grupo] = (b.name || '').match(/^(.*?)\s*\((grupo\s+[^)]+)\)\s*$/i) || [null, b.name || '', null];
                return (
                  <div className="px-1 py-px uppercase">
                    {/* Cortar SOLO entre palabras (break-words partía «REGGAETÓ/N»); letra compacta para
                        que «INTERMEDIO» o «ACRODANCE» quepan enteras en la media columna de un salón */}
                    <div className="text-[9.5px] font-bold leading-[1.1] tracking-tight [overflow-wrap:normal] [word-break:keep-all]">{base}</div>
                    {grupo && <div className="truncate text-[9.5px] font-bold leading-tight">{grupo}</div>}
                    {/* Karla 29-sep (3er video): sin las iniciales del profe y «CUPO: 4/16» en su propio
                        renglón — antes compartía línea con el grupo y se cortaba con «…».
                        Rentas/ensayos no tienen cupo real: sin la línea "0/12" que confunde. */}
                    {!muted && (
                      <div className="whitespace-nowrap text-[9.5px] leading-tight opacity-80">
                        CUPO: {en}{b.capacity ? `/${b.capacity}` : ''}
                      </div>
                    )}
                    {/* Sin franjas por salón (filtro activo o un solo salón), el salón no se ve en la columna */}
                    {b.classroom && subCols.length < 2 && <div className="truncate text-[9.5px] opacity-75">{b.classroom}</div>}
                  </div>
                );
              })(),
            };
          });
          const rangoLabel = `${monday.getDate()} ${monday.toLocaleDateString('es-MX', { month: 'short' })} – ${sunday.getDate()} ${sunday.toLocaleDateString('es-MX', { month: 'short' })}`;
          return (
            <div>
              {/* Navegación de semanas: ‹ rango › + Hoy (pedido de Karla 29-jul) */}
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <button onClick={() => shiftDay(-7)} aria-label="Semana anterior"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-content-2 ring-1 ring-line transition hover:bg-surface-2 hover:text-content">
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={() => { const el = dateRef.current; if (!el) return; try { (el as unknown as { showPicker: () => void }).showPicker(); } catch { el.focus(); } }}
                    className="relative flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm font-semibold text-content ring-1 ring-line transition hover:bg-surface-2">
                    <CalendarDays size={15} className="text-accent-ink" />
                    <span>{rangoLabel}</span>
                    <input ref={dateRef} type="date" value={dateInputValue}
                      onChange={(e) => { const v = e.target.value; if (!v) return; const [y, mm, d] = v.split('-').map(Number); setSelDate(new Date(y, mm - 1, d)); }}
                      className="pointer-events-none absolute inset-0 h-0 w-0 opacity-0" tabIndex={-1} />
                  </button>
                  <button onClick={() => shiftDay(7)} aria-label="Semana siguiente"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-content-2 ring-1 ring-line transition hover:bg-surface-2 hover:text-content">
                    <ChevronRight size={18} />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {!weekHasToday && (
                    <button onClick={() => setSelDate(new Date())}
                      className="rounded-md bg-accent/15 px-2.5 py-1 text-xs font-semibold text-accent-ink ring-1 ring-accent/25 transition hover:bg-accent/25">Esta semana</button>
                  )}
                  <span className="text-xs text-content-3">{visible.length} clase{visible.length === 1 ? '' : 's'}</span>
                </div>
              </div>
              {/* Semana completa visible sin scroll (8–22 compacto, como Kydemy) — pedido Carlos 2-jul */}
              {/* 48 px/h (antes 34): nombre completo hasta 3 renglones («REGGAETÓN / NIVEL / ABIERTO») + «CUPO: x/y»
                  en una clase de 1 h sin que se corte (Karla 29-sep). Con 42 el cupo de esa clase quedaba a la mitad. */}
              <TimeGrid columns={cols} blocks={gridBlocks} hourHeight={48} hourStart={8} hourEnd={22}
                subCols={subCols}
                hideOutOfRange nowLine={weekHasToday} minColWidth={esMovil ? (subCols.length > 1 ? 150 : 104) : 128}
                // Columna = día de la semana → el hueco trae el weekday y la hora ya redondeada.
                // (8-oct-2026) también la FECHA de esa columna: una renta o ensayo de un día la necesita.
                onSlotClick={esAdmin ? (colId, startMin) => abrirAltaEn({
                  weekday: Number(colId), start_time: hhmmDeMin(startMin), date: dateOfWd[Number(colId)],
                }) : undefined} />
              <p className="mt-2 text-center text-xs text-content-3">
                Horario semanal recurrente · la columna resaltada es hoy · clases pasadas atenuadas
                {!hideRentals && ' · rentas/ensayos en gris rayado'}
                {esAdmin && ' · clic en un hueco para agregar una clase a esa hora'}
              </p>
            </div>
          );
        })()
      ) : view === 'mes' ? (
        /* ===== MES (rejilla del mes: lo que pidió para «pasarse al siguiente mes») ===== */
        (() => {
          const y = mesVisto.getFullYear(), mo = mesVisto.getMonth();
          const primero = new Date(y, mo, 1);
          const dow = (primero.getDay() + 6) % 7;                 // 0 = lunes
          const inicio = new Date(y, mo, 1 - dow);
          const celdas = Array.from({ length: 42 }, (_, i) => new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + i));
          const hoyStr = ymd(new Date());
          const delDia = (d: Date) => byDay(d.getDay()).filter((b) => isActiveOn(b, ymd(d)));
          const totalMes = celdas.filter((d) => d.getMonth() === mo).reduce((s, d) => s + delDia(d).length, 0);
          const mesLabel = capitalize(mesVisto.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }));
          const esteMes = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); };
          const mueve = (n: number) => setMesVisto(new Date(y, mo + n, 1));
          return (
            <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
              <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
                <button onClick={() => mueve(-1)} aria-label="Mes anterior"
                  className="rounded-lg p-1.5 text-content-3 transition hover:bg-surface-2 hover:text-content"><ChevronLeft size={16} /></button>
                <span className="font-display text-sm font-bold text-content">{mesLabel}</span>
                <button onClick={() => mueve(1)} aria-label="Mes siguiente"
                  className="rounded-lg p-1.5 text-content-3 transition hover:bg-surface-2 hover:text-content"><ChevronRight size={16} /></button>
                {mesVisto.getTime() !== esteMes().getTime() && (
                  <button onClick={() => setMesVisto(esteMes())}
                    className="rounded-lg border border-line px-2 py-0.5 text-xs font-medium text-content-2 transition hover:bg-surface-2">Este mes</button>
                )}
                <span className="ml-auto text-xs text-content-3">{totalMes} {totalMes === 1 ? 'clase en el mes' : 'clases en el mes'}</span>
              </div>
              <div className="grid grid-cols-7 border-b border-line bg-surface-2/60 text-center text-[11px] font-semibold uppercase tracking-wide text-content-3">
                {ORDER.map((d) => <div key={d} className="py-1.5">{DAYS_SHORT[d]}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {celdas.map((d, i) => {
                  const fuera = d.getMonth() !== mo;
                  const esHoy = ymd(d) === hoyStr;
                  const cs = delDia(d);
                  return (
                    <button key={i} onClick={() => { setSelDate(d); setView('hoy'); }}
                      title={cs.length ? `${cs.length} clase(s) — abrir el día` : 'Abrir el día'}
                      className={`min-h-[86px] border-b border-r border-line p-1.5 text-left align-top transition hover:bg-surface-2 ${fuera ? 'bg-surface-2/30' : ''}`}>
                      {/* Día + cuántas clases tiene: es el dato que se lee de un golpe en un mes */}
                      <div className="mb-1 flex items-center justify-between gap-1">
                        <span className={`text-[11px] font-semibold tabular-nums ${
                          esHoy ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-on-accent'
                          : fuera ? 'text-content-3/60' : 'text-content-2'}`}>{d.getDate()}</span>
                        {cs.length > 0 && (
                          <span className="rounded bg-surface-2 px-1 text-[10px] font-semibold tabular-nums text-content-3">{cs.length}</span>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        {cs.slice(0, 3).map((b) => (
                          <div key={b.schedule_id} className="flex items-center gap-1 truncate text-[10px] leading-tight text-content-2">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: colorClase(b.color) }} />
                            <span className="shrink-0 tabular-nums text-content-3">{hhmm(b.start_time)}</span>
                            <span className="truncate">{b.name}</span>
                          </div>
                        ))}
                        {cs.length > 3 && <div className="text-[10px] font-semibold text-accent-ink">+{cs.length - 3} más</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()
      ) : view === 'cards' ? (
        /* ===== TARJETAS ===== */
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {classRows.length === 0 && (
            <p className="col-span-full rounded-xl border border-line bg-surface px-4 py-6 text-center text-sm text-content-3">
              {fEstado === 'activas' && cicloTerminado?.proximo
                ? <>Hoy no hay ninguna clase corriendo: tus {cicloTerminado.clases} clases arrancan el{' '}
                    {new Date(`${cicloTerminado.proximo}T00:00:00`).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}.
                    Están en <b className="text-content-2">Planificadas</b>.</>
                : 'Sin clases con ese filtro.'}
            </p>
          )}
          {classRows.map((c) => {
            const en = enrolledOf[c.class_id] ?? 0;
            const cap = c.capacity || 0;
            const pct = cap ? Math.min(100, Math.round((en / cap) * 100)) : 0;
            const est = estadoDe(c);
            return (
              <button key={c.class_id} onClick={() => navigate(`/clases/${c.class_id}`)}
                className="overflow-hidden rounded-xl border border-line bg-surface text-left shadow-sm transition hover:border-accent/40 hover:shadow-md">
                {c.image_url
                  ? <ClassCover url={c.image_url} className="h-28 w-full object-cover" />
                  : <div className="h-2 w-full" style={{ background: colorClase(c.color) }} />}
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold leading-tight text-content">{c.name}</span>
                    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                      est === 'activas' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                      : est === 'planificadas' ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400'
                      : 'bg-surface-2 text-content-3'}`}>
                      {est === 'activas' ? 'Activa' : est === 'planificadas' ? 'Planificada' : 'Archivada'}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-content-3">
                    {c.teacher || 'Sin profe'}{c.classroom ? ` · ${c.classroom}` : ''}
                  </div>
                  <div className="mt-1 text-xs">
                    <span className="text-content-2">{c.days.map((d) => DAYS_SHORT[d]).join(', ')}</span>
                    <span className="text-content-3"> · {hhmm(c.start_time)}–{hhmm(c.end_time)}</span>
                  </div>
                  <div className="mt-2.5 flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-2">
                      <div className="h-full rounded-full bg-content" style={{ width: `${pct}%` }} />
                    </div>
                    <span className={`shrink-0 text-xs tabular-nums ${cupoTone(en, c.capacity)}`}>{cupoText(en, c.capacity)}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        /* ===== TABLA ===== */
        <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
          <table className="hidden w-full text-sm sm:table">
            <thead className="border-b border-line bg-surface-2 text-content-3">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">Clase</th>
                <th className="px-4 py-2.5 text-left font-medium">Profesor</th>
                <th className="px-4 py-2.5 text-left font-medium">Salón</th>
                <th className="px-4 py-2.5 text-left font-medium">Horario</th>
                <th className="px-4 py-2.5 text-left font-medium">Cupo</th>
              </tr>
            </thead>
            <tbody>
              {classRows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-content-3">
                  {fEstado === 'activas' && cicloTerminado?.proximo
                    ? <>Hoy no hay ninguna clase corriendo: tus {cicloTerminado.clases} clases arrancan el{' '}
                        {new Date(`${cicloTerminado.proximo}T00:00:00`).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}.
                        Están en <b className="text-content-2">Planificadas</b>.</>
                    : 'Sin clases con ese filtro.'}
                </td></tr>
              )}
              {classRows.map((c) => {
                const en = enrolledOf[c.class_id] ?? 0;
                const cap = c.capacity || 0;
                const pct = cap ? Math.min(100, Math.round((en / cap) * 100)) : 0;
                const full = cap && en >= cap;
                return (
                  <tr key={c.class_id} onClick={() => navigate(`/clases/${c.class_id}`)}
                    className="cursor-pointer border-t border-line transition even:bg-surface-2/40 hover:bg-surface-2">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        {c.image_url
                          ? <ClassCover url={c.image_url} className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                          : <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: colorClase(c.color) }} />}
                        <span className="font-medium text-content">{c.name}</span>
                        <EndedChip end={c.end_date} />
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-content-2">{c.teacher || '—'}</td>
                    <td className="px-4 py-2.5 text-content-2">{c.classroom || '—'}</td>
                    <td className="px-4 py-2.5 text-content-2">
                      <span className="text-content">{c.days.map((d) => DAYS_SHORT[d]).join(', ')}</span>
                      <span className="text-content-3"> · {hhmm(c.start_time)}–{hhmm(c.end_time)}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {/* Karla 30-sep: la barrita va neutra (blanca en oscuro), no del color de la clase;
                            el color de ocupación (verde/ámbar/rojo) se queda solo en el texto. */}
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-2">
                          <div className="h-full rounded-full bg-content" style={{ width: `${pct}%` }} />
                        </div>
                        <span className={`tabular-nums ${cupoTone(en, c.capacity)}`}>{cupoText(en, c.capacity)}{full ? ' · llena' : ''}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="divide-y divide-line sm:hidden">
            {classRows.length === 0 && <div className="px-4 py-6 text-center text-content-3">Sin clases con ese filtro.</div>}
            {classRows.map((c) => {
              const en = enrolledOf[c.class_id] ?? 0;
              const cap = c.capacity || 0;
              const pct = cap ? Math.min(100, Math.round((en / cap) * 100)) : 0;
              const full = cap && en >= cap;
              return (
                <button key={c.class_id} onClick={() => navigate(`/clases/${c.class_id}`)}
                  className="block w-full px-4 py-3 text-left transition hover:bg-surface-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2.5">
                      {c.image_url
                        ? <ClassCover url={c.image_url} className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                        : <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: colorClase(c.color) }} />}
                      <span className="truncate font-medium text-content">{c.name}</span>
                      <EndedChip end={c.end_date} />
                    </div>
                    <span className={`shrink-0 text-sm tabular-nums ${cupoTone(en, c.capacity)}`}>{cupoText(en, c.capacity)}{full ? ' · llena' : ''}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-content-3">
                    {c.teacher && <span>{c.teacher}</span>}
                    {c.classroom && <span>· {c.classroom}</span>}
                  </div>
                  <div className="mt-0.5 text-xs">
                    <span className="text-content-2">{c.days.map((d) => DAYS_SHORT[d]).join(', ')}</span>
                    <span className="text-content-3"> · {hhmm(c.start_time)}–{hhmm(c.end_time)}</span>
                  </div>
                  {cap > 0 && (
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
                      {/* Karla 30-sep: igual que en la tabla de compu, barrita neutra (este es el renglón de CELULAR). */}
                      <div className="h-full rounded-full bg-content" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
